/**
 * Instana EUM (website monitoring) — links browser page loads to backend traces.
 * Snippet aligned with Instana website monitoring setup (1.8.1 agent).
 * @see https://www.ibm.com/docs/en/instana-observability/current?topic=websites-website-monitoring
 */

import { getUserDisplayName } from 'src/models/user/utils';

import type { User } from 'src/models/user/_types';

const INSTANA_EUM_KEY = import.meta.env.VITE_INSTANA_EUM_KEY || '';
const INSTANA_EUM_REPORTING_URL =
  import.meta.env.VITE_INSTANA_EUM_REPORTING_URL || '';

const EUM_SCRIPT_SRC = 'https://eum.instana.io/1.8.1/eum.min.js';
const EUM_SCRIPT_INTEGRITY =
  'sha384-qFzHZ5BC7HOPEBSYkbYSv+DBWrG34P1QW9mIaCR41db6yOJNYmH4antW6KLkc6v1';
const INSTANA_IGNORED_URLS = [
  /\/rails\/active_storage\/blobs\/redirect\//,
];

declare global {
  interface Window {
    ineum?: (...args: unknown[]) => void;
    InstanaEumObject?: string;
  }
}

/** Instana inline snippet: stub + queue until eum.min.js loads. */
function ensureIneumStub(): void {
  if (window.InstanaEumObject) return;

  window.InstanaEumObject = 'ineum';
  const ineum = (...args: unknown[]) => {
    ineum.q.push(args);
  };
  ineum.q = [];
  ineum.v = 2;
  ineum.l = Date.now();
  window.ineum = ineum;
}

function loadIneumScript(): void {
  if (document.querySelector(`script[src="${EUM_SCRIPT_SRC}"]`)) return;

  const script = document.createElement('script');
  script.defer = true;
  script.src = EUM_SCRIPT_SRC;
  script.crossOrigin = 'anonymous';
  script.integrity = EUM_SCRIPT_INTEGRITY;

  document.head.appendChild(script);
}

/**
 * Initialize Instana EUM when website key and reporting URL are configured.
 */
export function initializeInstana(): void {
  const key = INSTANA_EUM_KEY.trim();
  const reportingUrl = INSTANA_EUM_REPORTING_URL.trim();
  if (!key || !reportingUrl) return;

  ensureIneumStub();
  window.ineum?.('reportingUrl', reportingUrl);
  window.ineum?.('key', key);
  window.ineum?.('ignoreUrls', INSTANA_IGNORED_URLS);
  window.ineum?.('trackSessions');
  window.ineum?.('autoPageDetection', true);
  window.ineum?.('meta', 'platform_type', 'web');
  loadIneumScript();
}

/**
 * Identify the logged-in user for Instana session correlation.
 */
export function setupInstanaUserContext(user: User | null | undefined): void {
  if (!window.ineum) return;

  if (user) {
    window.ineum(
      'user',
      user.id?.toString(),
      getUserDisplayName(user),
      user.email
    );
  }
}
